import { CloseOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Divider, Modal, theme } from 'antd';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { formatFileSize, formatLinearized, formatPageSize, formatPdfDate, formatText } from './documentProperties';
import { getLoadedPdfjs } from './pdfjsViewerModule';

interface Props {
  open: boolean;
  pdfDocument: PDFDocumentProxy | null;
  /** pdf.js reports the size of the page currently in view. */
  currentPage: number;
  pagesRotation: number;
  onClose: () => void;
}

interface Row {
  key: string;
  label: string;
  value: string;
}

export const DocumentPropertiesModal: React.FC<Props> = ({ open, pdfDocument, currentPage, pagesRotation, onClose }) => {
  const { token } = theme.useToken();
  const [groups, setGroups] = useState<Row[][]>([]);

  useEffect(() => {
    if (!open || !pdfDocument) {
      return;
    }
    let cancelled = false;

    const load = async () => {
      const lib = getLoadedPdfjs();
      const [{ info }, { length }, page] = await Promise.all([
        pdfDocument.getMetadata(),
        pdfDocument.getDownloadInfo(),
        pdfDocument.getPage(currentPage),
      ]);
      if (cancelled) {
        return;
      }

      const meta = info as Record<string, unknown>;
      const toDate = (raw: string) => lib?.PDFDateString.toDateObject(raw) ?? null;
      // For blob sources this falls back to pdf.js's own "document.pdf", exactly as the
      // old viewer did — there is no better name to recover from an object URL.
      const url = pdfDocument.loadingParams?.url;
      const fileName = lib && url ? lib.getPdfFilenameFromUrl(String(url)) : null;

      // Field order and grouping follow the original viewer exactly, including where it
      // placed its three <div class="separator"> elements. Labels use its German wording.
      // Rows the document does not carry are dropped instead of printed as dashes; a
      // group that empties out disappears with them, and so does its divider.
      const candidates: Array<Array<[string, string, string | null]>> = [
        [
          ['fileName', 'Dateiname', formatText(fileName)],
          ['fileSize', 'Dateigröße', formatFileSize(length)],
        ],
        [
          ['title', 'Titel', formatText(meta.Title)],
          ['author', 'Autor', formatText(meta.Author)],
          ['subject', 'Thema', formatText(meta.Subject)],
          ['keywords', 'Stichwörter', formatText(meta.Keywords)],
          ['creationDate', 'Erstelldatum', formatPdfDate(meta.CreationDate, toDate)],
          ['modDate', 'Bearbeitungsdatum', formatPdfDate(meta.ModDate, toDate)],
          ['creator', 'Anwendung', formatText(meta.Creator)],
        ],
        [
          ['producer', 'PDF erstellt mit', formatText(meta.Producer)],
          ['version', 'PDF-Version', formatText(meta.PDFFormatVersion)],
          ['pageCount', 'Seitenzahl', String(pdfDocument.numPages)],
          ['pageSize', 'Seitengröße', formatPageSize(page, pagesRotation)],
        ],
        [['linearized', 'Schnelle Webanzeige', formatLinearized(meta.IsLinearized)]],
      ];

      setGroups(
        candidates
          .map(group =>
            group
              .filter((entry): entry is [string, string, string] => entry[2] !== null)
              .map(([key, label, value]) => ({ key, label, value }))
          )
          .filter(group => group.length > 0)
      );
    };

    // Nothing to report if the document went away between opening the dialog and this
    // call; an empty list beats an unhandled rejection.
    load().catch(() => {
      if (!cancelled) {
        setGroups([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, pdfDocument, currentPage, pagesRotation]);

  // One grid for every row, not one per group: separate grids each size their own label
  // column, which left the values standing at four different x-positions. Groups are
  // separated by a Divider spanning both columns instead.
  const gridStyle = css`
    display: grid;
    margin-top: ${token.marginSM}px;
    grid-template-columns: auto 1fr;
    column-gap: ${token.margin}px;
    row-gap: ${token.paddingXXS}px;
    align-items: baseline;
    font-size: ${token.fontSizeSM}px;
  `;

  // Spans both columns, as the old viewer's `.separator` did.
  const dividerStyle = css`
    grid-column: 1 / -1;
  `;

  const labelStyle = css`
    color: ${token.colorTextSecondary};
    white-space: nowrap;
  `;

  const valueStyle = css`
    color: ${token.colorText};
    word-break: break-word;
  `;

  return (
    <Modal
      title="Dokumenteigenschaften"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ icon: <CloseOutlined /> }}
      okText="Schließen"
      width={550}
    >
      <div className={gridStyle}>
        {groups.map((group, index) => (
          <Fragment key={group[0].key}>
            {index > 0 && <Divider size="small" className={dividerStyle} />}
            {group.map(row => (
              <Fragment key={row.key}>
                <span className={labelStyle}>{row.label}:</span>
                <span className={valueStyle}>{row.value}</span>
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </Modal>
  );
};
